import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MunicipalityOption,
  Region,
  SessionUser,
  Tier,
  UpdateSettingsBody,
} from '@auctionradar/shared';
import { api } from '../api/client';

const ALL_TIERS: Tier[] = ['A', 'B', 'C', 'D'];

type SettingsResponse = {
  user: SessionUser;
  municipalities: MunicipalityOption[];
};

export function SettingsPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['settings'] as const,
    queryFn: () => api.getSettings(),
  });

  const mutation = useMutation({
    mutationFn: (body: UpdateSettingsBody) => api.updateSettings(body),
    onSuccess: (next) => {
      qc.setQueryData<SettingsResponse>(['settings'], (prev) =>
        prev ? { ...prev, user: next.user } : prev,
      );
    },
  });

  const [tiers, setTiers] = useState<Set<Tier>>(new Set());
  const [muniMode, setMuniMode] = useState<'all' | 'specific'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Hydrate local form state once the settings query resolves.
  useEffect(() => {
    const u = settingsQuery.data?.user;
    if (!u) return;
    setTiers(new Set(u.alertTiers));
    if (u.targetMunicipalities == null) {
      setMuniMode('all');
      setSelected(new Set());
    } else {
      setMuniMode('specific');
      setSelected(new Set(u.targetMunicipalities));
    }
  }, [settingsQuery.data?.user]);

  const munisByRegion = useMemo(() => {
    const opts = settingsQuery.data?.municipalities ?? [];
    const groups = new Map<Region, MunicipalityOption[]>();
    for (const o of opts) {
      const arr = groups.get(o.region);
      if (arr) arr.push(o);
      else groups.set(o.region, [o]);
    }
    return groups;
  }, [settingsQuery.data?.municipalities]);

  const toggleTier = (t: Tier) => {
    setTiers((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const toggleMuni = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const onSave = () => {
    if (tiers.size === 0) return;
    const body: UpdateSettingsBody = {
      alertTiers: [...tiers].sort() as Tier[],
      targetMunicipalities:
        muniMode === 'all' ? null : [...selected].sort(),
    };
    mutation.mutate(body);
  };

  if (settingsQuery.isLoading) {
    return <div className="h-32 animate-pulse rounded-lg bg-white" />;
  }
  if (settingsQuery.isError || !settingsQuery.data) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        Αποτυχία φόρτωσης settings.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">
          {settingsQuery.data.user.name} · role:{' '}
          <span className="font-medium">{settingsQuery.data.user.role}</span>
        </p>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-700">Alert tiers</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Επίπεδα leads για τα οποία ο notifier σου στέλνει την εβδομαδιαία λίστα.
        </p>
        <div className="mt-3 flex gap-2">
          {ALL_TIERS.map((t) => {
            const active = tiers.has(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleTier(t)}
                className={`rounded-full px-4 py-1 text-sm font-bold tracking-wide transition ${
                  active
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
        {tiers.size === 0 && (
          <p className="mt-2 text-xs text-rose-600">
            Πρέπει να επιλέξεις τουλάχιστον ένα tier.
          </p>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-700">
          Target municipalities
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Όλες οι ζώνες ή μόνο μια συγκεκριμένη επιλογή.
        </p>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => setMuniMode('all')}
            className={`rounded px-3 py-1 text-sm transition ${
              muniMode === 'all'
                ? 'bg-slate-900 text-white'
                : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            Όλα
          </button>
          <button
            type="button"
            onClick={() => setMuniMode('specific')}
            className={`rounded px-3 py-1 text-sm transition ${
              muniMode === 'specific'
                ? 'bg-slate-900 text-white'
                : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            Επιλογή
          </button>
        </div>

        {muniMode === 'specific' && (
          <div className="mt-4 space-y-4">
            {[...munisByRegion.entries()].map(([region, opts]) => (
              <div key={region}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {region === 'corinth' ? 'Κορινθία' : 'Αττική'} ({opts.length})
                </h3>
                <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4">
                  {opts.map((o) => {
                    const checked = selected.has(o.slug);
                    return (
                      <label
                        key={o.slug}
                        className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs transition ${
                          checked
                            ? 'bg-slate-100 font-medium text-slate-900'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="h-3 w-3 accent-slate-900"
                          checked={checked}
                          onChange={() => toggleMuni(o.slug)}
                        />
                        <span className="truncate">{o.nameEl}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            {selected.size === 0 && (
              <p className="text-xs text-amber-700">
                Δεν έχεις επιλέξει κανένα δήμο — δεν θα παίρνεις alerts.
              </p>
            )}
          </div>
        )}
      </section>

      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={tiers.size === 0 || mutation.isPending}
          className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-40"
        >
          {mutation.isPending ? 'Αποθήκευση…' : 'Αποθήκευση'}
        </button>
        {mutation.isSuccess && (
          <span className="text-xs text-emerald-700">✅ Αποθηκεύτηκε</span>
        )}
        {mutation.isError && (
          <span className="text-xs text-rose-700">
            Αποτυχία: {(mutation.error as Error).message}
          </span>
        )}
      </div>
    </div>
  );
}
